/**
 * ComplianceGate — the ADR-025 §6.4 access gate (replaces the disabled TOSGate).
 *
 * Renders between auth and the app, in BOTH dev and Clerk branches. One server
 * call (GET /api/v1/eligibility/me) drives the whole flow:
 *
 *   blocked          → blocked screen (no product chrome, reinstatement path)
 *   review_required  → review screen (state explained, data untouched)
 *   tos not accepted → Terms step
 *   declaration old  → personal-use declaration step
 *   consumer         → children
 *   pending (rare)   → holding screen with retry
 *
 * FAIL-CLOSED: any fetch error or non-200 renders verify_failed with a Retry
 * button — children are never rendered from an unverified state. This inverts
 * the old TOSGate passthrough and the old fail-open acceptance flow.
 *
 * Runs above AppLayout, so authFetch's token provider is not set yet — uses
 * getAuthToken() from useAuth() directly.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from './AuthWrapper';
import TermsOfService from './TermsOfService';
import PersonalUseDeclaration from './PersonalUseDeclaration';
import { getDeviceId } from '../../utils/deviceId';

const SUPPORT_EMAIL = 'support@mindfrontier.io';

// ---------------------------------------------------------------------------
// Shell — mirrors the SignInPage register (Inter, #fafafa, brand radials).
// ---------------------------------------------------------------------------

export const GateShell = ({ eyebrow, children, maxWidth = 560 }) => (
  <div
    style={{
      minHeight: '100dvh',
      background: '#fafafa',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      color: '#262626',
    }}
  >
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: '-10% -10% 0 -10%',
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 50% 40% at 80% 30%, rgba(244,63,94,0.06), transparent 60%), '
          + 'radial-gradient(ellipse 60% 50% at 20% 70%, rgba(99,102,241,0.05), transparent 60%)',
        filter: 'blur(20px)',
      }}
    />
    <a
      href="/"
      style={{
        position: 'absolute', top: 24, left: 32, zIndex: 1,
        fontFamily: '"Inter", system-ui, sans-serif',
        fontWeight: 600, fontSize: 17, letterSpacing: '-0.02em',
        color: '#171717', textDecoration: 'none',
      }}
    >
      Heyward
    </a>
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '80px 20px 40px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: '100%', maxWidth, margin: 'auto 0' }}
      >
        {eyebrow && (
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              margin: '0 auto 18px',
              width: '100%', justifyContent: 'center',
              fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
              fontSize: 10.5, letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#737373',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 7, height: 7, borderRadius: '50%',
                background: 'conic-gradient(from 180deg at 50% 50%, #f43f5e, #6366f1, #f43f5e)',
              }}
            />
            {eyebrow}
          </div>
        )}
        {children}
      </motion.div>
    </div>
  </div>
);

const headlineStyle = {
  fontFamily: '"Instrument Serif", Georgia, serif',
  fontWeight: 400,
  fontSize: 'clamp(28px, 3.4vw, 36px)',
  lineHeight: 1.08,
  letterSpacing: '-0.02em',
  color: '#171717',
  textAlign: 'center',
  margin: '0 0 10px',
};

const bodyStyle = {
  textAlign: 'center',
  color: '#525252',
  fontSize: 14.5,
  lineHeight: 1.6,
  margin: '0 auto 22px',
  maxWidth: '46ch',
};

const primaryButtonStyle = (enabled) => ({
  display: 'block',
  width: '100%',
  padding: '12px 16px',
  borderRadius: 10,
  border: 'none',
  fontFamily: '"Inter", system-ui, sans-serif',
  fontWeight: 500,
  fontSize: 14.5,
  cursor: enabled ? 'pointer' : 'not-allowed',
  background: enabled ? '#292524' : '#e5e5e5',
  color: enabled ? '#ffffff' : '#a3a3a3',
  transition: 'background 150ms ease',
});

const quietLinkStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
  fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase',
  color: '#737373', textDecoration: 'underline dotted', textUnderlineOffset: 3,
  padding: '4px 8px',
};

const HoldScreen = ({ eyebrow, title, children, onRetry, onSignOut }) => (
  <GateShell eyebrow={eyebrow} maxWidth={460}>
    <h1 style={headlineStyle}>{title}</h1>
    <div style={bodyStyle}>{children}</div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ ...primaryButtonStyle(true), maxWidth: 220 }}>
          Try again
        </button>
      )}
      {onSignOut && (
        <button type="button" onClick={onSignOut} style={quietLinkStyle}>
          Sign out
        </button>
      )}
    </div>
  </GateShell>
);

// ---------------------------------------------------------------------------
// Gate
// ---------------------------------------------------------------------------

const derivePhase = (payload) => {
  if (!payload || !payload.access_status) return 'verify_failed';
  if (payload.access_status === 'blocked') return 'blocked';
  if (payload.access_status === 'review_required') return 'review_required';
  if (!payload.tos?.accepted) return 'step_tos';
  if (!payload.declaration?.accepted) return 'step_declaration';
  if (payload.access_status === 'consumer') return 'ready';
  return 'pending_hold';
};

const ComplianceGate = ({ children }) => {
  const { user, getAuthToken, signOut } = useAuth();
  const [phase, setPhase] = useState('checking');
  const [payload, setPayload] = useState(null);

  const fetchState = useCallback(async () => {
    setPhase('checking');
    try {
      const baseURL = import.meta.env.REACT_APP_API_URL || window.location.origin;
      const token = getAuthToken ? await getAuthToken() : null;
      const res = await fetch(`${baseURL}/api/v1/eligibility/me`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          'X-Heyward-Device': getDeviceId(),
        },
      });
      if (!res.ok) throw new Error(`eligibility returned ${res.status}`);
      const data = await res.json();
      setPayload(data);
      setPhase(derivePhase(data));
    } catch (err) {
      console.error('ComplianceGate: could not verify access:', err);
      setPayload(null);
      setPhase('verify_failed');   // fail closed — never render children
    }
  }, [getAuthToken]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Declaration POST returns the fresh state — consume it directly.
  const onDeclarationState = useCallback((data) => {
    if (data) {
      setPayload(data);
      setPhase(derivePhase(data));
    } else {
      fetchState();
    }
  }, [fetchState]);

  if (phase === 'ready') return children;

  if (phase === 'checking') {
    return (
      <GateShell maxWidth={420}>
        <h1 style={headlineStyle}>Checking your access…</h1>
        <p style={bodyStyle}>One moment.</p>
      </GateShell>
    );
  }

  if (phase === 'step_tos') {
    return (
      <GateShell eyebrow="Getting set up · 1 of 2" maxWidth={720}>
        <TermsOfService
          userId={user?.userId}
          onAccept={fetchState}
          getAuthToken={getAuthToken}
        />
      </GateShell>
    );
  }

  if (phase === 'step_declaration') {
    return (
      <GateShell eyebrow="Getting set up · 2 of 2" maxWidth={560}>
        <PersonalUseDeclaration
          declarationVersion={payload?.declaration?.current_version}
          getAuthToken={getAuthToken}
          onStateChange={onDeclarationState}
        />
      </GateShell>
    );
  }

  if (phase === 'review_required') {
    return (
      <HoldScreen
        eyebrow="Access review"
        title="Your access is being reviewed."
        onSignOut={signOut}
      >
        A person is looking at your account setup. Everything you have saved
        stays exactly as it is. We will email you when the review is done. If
        you think this is a mistake, write to{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#171717' }}>{SUPPORT_EMAIL}</a>.
      </HoldScreen>
    );
  }

  if (phase === 'blocked') {
    return (
      <HoldScreen
        eyebrow="Account status"
        title="Access to this account is blocked."
        onSignOut={signOut}
      >
        Your data has not been deleted. If you believe this decision is wrong,
        write to{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#171717' }}>{SUPPORT_EMAIL}</a>{' '}
        and we will look again.
      </HoldScreen>
    );
  }

  if (phase === 'pending_hold') {
    return (
      <HoldScreen
        eyebrow="Almost there"
        title="Setting up your access…"
        onRetry={fetchState}
        onSignOut={signOut}
      >
        This usually takes a moment. If nothing changes, try again.
      </HoldScreen>
    );
  }

  // verify_failed — the fail-closed default.
  return (
    <HoldScreen
      eyebrow="Connection"
      title="We can't verify your access right now."
      onRetry={fetchState}
      onSignOut={signOut}
    >
      Heyward could not reach the access service, so nothing is shown until it
      can. Your data is safe. Check your connection and try again.
    </HoldScreen>
  );
};

export default ComplianceGate;
